import { getSiteFeatureById, MEETINGS, SiteFeature, USERS } from 'app/constants/enums/SiteFeature';
import { SiteFeatureId } from 'app/constants/enums/SiteFeature/data';
import {
  Brand,
  BrandSiteFeature,
  Configuration,
  MeetingsConfig,
  Organization,
  OrganizationDetails,
  OrganizationSiteFeature,
  UsersConfig,
} from 'app/constants/schemas';
import { IMeetingsConfig } from 'app/constants/schemas/SiteFeatures/MeetingsConfig';
import { IUsersConfig } from 'app/constants/schemas/SiteFeatures/UsersConfig';
import { AppState, AppThunk } from 'app/store';
import { Entity, EntityFields, EntityStringId } from 'app/types/entity';
import { absurd, pipe } from 'fp-ts/lib/function';
import { List } from 'immutable';
import { AppDispatch } from 'redux';
import { batchActions } from 'redux-batched-actions';
import { addListElement, removeListElement, setAttribute } from '.';
import { getEntityOfType, getEntityOfTypeOrThrow } from '../selectors';
import { entityCreator, IIdAction } from './next';
import * as OptionFP from 'fp-ts/lib/Option';
import { IOrganizationSiteFeature } from 'app/constants/schemas/SiteFeatures/OrganizationSiteFeature';
import { IBrandSiteFeature } from 'app/constants/schemas/SiteFeatures/BrandSiteFeature';
import { IOrganization } from 'app/constants/schemas/Organization';
import { IOrganizationDetails } from 'app/constants/schemas/OrganizationDetails';

const createOrganizationSiteFeatureEntity = entityCreator(OrganizationSiteFeature, () => ({
  configuration: null,
}));
const createOrganizationSiteFeatureEntityNullConfig = (feature: SiteFeature) =>
  createOrganizationSiteFeatureEntity({ siteFeature: feature, configuration: null });
const createMeetingsConfig = entityCreator(MeetingsConfig, () => ({
  supportUrl: null,
}));
const createUsersConfig = entityCreator(UsersConfig, () => ({
  externalUserProfileUrl: null,
  authProviders: List(),
}));

const removeBrandSiteFeature = (brandId: EntityStringId, featureId: SiteFeatureId): AppThunk<void> => (
  dispatch,
  getState,
) => {
  pipe(
    getBrandFeature(getState(), brandId, featureId),
    OptionFP.map(v => {
      dispatch(
        removeListElement({
          type: Brand,
          id: brandId,
          value: v.id,
          attribute: 'siteFeatures',
        }),
      );
    }),
  );
};

/**
 * Attempt to get the specified site feature from the organization
 * @param state
 * @param organizationId -- The Id of the organization to search through.
 * @param featureId -- The id of the site feature to search for
 * @returns The site feature configured on the organization if one was found or None.
 */
export const getOrganizationFeature = (
  state: AppState,
  organizationId: EntityStringId,
  featureId: SiteFeatureId,
): OptionFP.Option<Entity<IOrganizationSiteFeature>> => {
  // It's possible that one or the other of the Organization / OrganizationDetails will be in the store.
  // The value can be gotten from either entity so this helper attempts both but prefers OrganizationDetails
  // since that value is used in the editor.
  const org: Entity<IOrganization> | Entity<IOrganizationDetails> =
    getEntityOfType(state, OrganizationDetails, organizationId) ??
    getEntityOfTypeOrThrow(state, Organization, organizationId);
  return OptionFP.fromNullable(
    org.siteFeatures
    .map(fId => getEntityOfTypeOrThrow(state, OrganizationSiteFeature, fId))
    .find(f => f.siteFeature.id === featureId),
  );
};

/**
 * Attempt to get the specified site feature from the brand
 * @param state
 * @param brandId -- The id of the brand to search through.
 * @param featureId -- The id of the feature to search for.
 * @returns the found feature or none if none could be found.
 */
export const getBrandFeature = (
  state: AppState,
  brandId: EntityStringId,
  featureId: SiteFeatureId,
): OptionFP.Option<Entity<IBrandSiteFeature>> => {
  return OptionFP.fromNullable(
    getEntityOfTypeOrThrow(state, Brand, brandId).siteFeatures
      .map(fId => getEntityOfTypeOrThrow(state, BrandSiteFeature, fId))
      .find(f => f.siteFeature.id === featureId),
  );
};

const createOrganizationSiteFeature = (organizationId: EntityStringId, feature: SiteFeatureId) => (
  dispatch: AppDispatch,
): IIdAction => {
  switch (feature) {
    case 'meetings': {
      const meetingConfig = createMeetingsConfig();
      const createMeetingFeatureAction = createOrganizationSiteFeatureEntity({
        siteFeature: MEETINGS,
        configuration: meetingConfig.id,
      });
      dispatch(batchActions([meetingConfig, createMeetingFeatureAction]));
      return createMeetingFeatureAction;
    }
    case 'users': {
      const usersConfig = createUsersConfig();
      const createSiteFeatureAction = createOrganizationSiteFeatureEntity({
        siteFeature: USERS,
        configuration: usersConfig.id,
      });
      dispatch(batchActions([usersConfig, createSiteFeatureAction]));
      return createSiteFeatureAction;
    }
    case 'dailyNews':
    case 'iknowIntegration':
    case 'materials':
    case 'wordsPage':
      return dispatch(createOrganizationSiteFeatureEntityNullConfig(getSiteFeatureById(feature)));
    default:
      return absurd(feature);
  }
};

const createBrandSiteFeatureEntity = entityCreator(BrandSiteFeature, () => ({
  configuration: null,
  enabled: true,
}));

const createAndAddBrandSiteFeature = (
  brandId: EntityStringId,
  overrides?: Partial<EntityFields<IBrandSiteFeature>>,
) => (dispatch: AppDispatch) => {
  const createAction = createBrandSiteFeatureEntity(overrides);
  const addListAction = addListElement({
    type: Brand,
    id: brandId,
    value: createAction.id,
    attribute: 'siteFeatures',
  });
  dispatch(batchActions([createAction, addListAction]));
  return createAction;
};

export const removeOrganizationSiteFeature = (
  organizationId: EntityStringId,
  feature: SiteFeatureId,
): AppThunk<void> => (dispatch, getState) => {
  const state = getState();
  const org = getEntityOfTypeOrThrow(state, OrganizationDetails, organizationId);
  const setFeatures = org.siteFeatures.map(fId => getEntityOfTypeOrThrow(state, OrganizationSiteFeature, fId));
  const featureToRemove = setFeatures.find(v => v.siteFeature.id === feature);
  // Only attempt to remove the feature if it exists on the organization
  if (featureToRemove) {
    dispatch(
      removeListElement({
        type: OrganizationDetails,
        value: featureToRemove.id,
        id: organizationId,
        attribute: 'siteFeatures',
      }),
    );
    dispatch(
      removeListElement({
        type: Organization,
        value: featureToRemove.id,
        id: organizationId,
        attribute: 'siteFeatures',
      }),
    );
  }
};

const createConfigForBrandSiteFeature = (
  feature: SiteFeatureId,
  brandId: EntityStringId,
): AppThunk<EntityStringId | null> => (dispatch, getState) => {
  const state = getState();
  const orgFeature = getOrganizationFeature(
    state,
    getEntityOfTypeOrThrow(state, Brand, brandId).organization,
    feature,
  );
  const config = pipe(
    orgFeature,
    OptionFP.mapNullable(({ configuration }) => configuration),
    OptionFP.map(id => getEntityOfTypeOrThrow(state, Configuration, id)),
  );

  switch (feature) {
    case 'meetings': {
      const meetingDefaults: Partial<EntityFields<IMeetingsConfig>> = pipe(
        config,
        OptionFP.fold(
          () => ({}),
          e => (e instanceof MeetingsConfig ? { supportUrl: e.supportUrl } : {}),
        ),
      );
      return dispatch(createMeetingsConfig(meetingDefaults)).id;
    }
    case 'users': {
      const userDefaults: Partial<EntityFields<IUsersConfig>> = pipe(
        config,
        OptionFP.fold(
          () => ({}),
          e =>
            e instanceof UsersConfig
              ? { externalUserProfileUrl: e.externalUserProfileUrl, authProviders: e.authProviders }
              : {},
        ),
      );
      return dispatch(createUsersConfig(userDefaults)).id;
    }
    case 'dailyNews':
    case 'iknowIntegration':
    case 'materials':
    case 'wordsPage':
      return null;
    default:
      return absurd(feature);
  }
};

/**
 * Add an organization site feature for the given feature.
 * @param organizationId The id of the organization to add the feature to
 * @param featureId The id of the site feature to add
 * @returns an action that adds the site feature (if it is not already part of the organization's configuration).
 */
export const addOrganizationSiteFeature = (
  organizationId: EntityStringId,
  featureId: SiteFeatureId,
): AppThunk<void> => (dispatch, getState) => {
  if (OptionFP.isNone(getOrganizationFeature(getState(), organizationId, featureId))) {
    const { id: actionId } = dispatch(createOrganizationSiteFeature(organizationId, featureId));
    dispatch(
      addListElement({
        type: OrganizationDetails,
        value: actionId,
        id: organizationId,
        attribute: 'siteFeatures',
      }),
    );
    dispatch(
      addListElement({
        type: Organization,
        value: actionId,
        id: organizationId,
        attribute: 'siteFeatures',
      }),
    );
  }
};

/**
 * Set the state of a brand site feature. This may result in deletion of the BrandSiteFeature
 * @param brandId -- the brand on which to set the feature.
 * @param featureId -- the id of the feature to set.
 * @param enabled -- whether or not the feature should be enabled.
 * @returns setAction
 */
export const setBrandFeatureState = (
  brandId: EntityStringId,
  featureId: SiteFeatureId,
  enabled: boolean,
): AppThunk<void> => (dispatch, getState) => {
  const state = getState();
  // Grab entities needed for calculating the merge states.
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const organizationFeature = getOrganizationFeature(state, brand.organization, featureId);
  const brandFeature = getBrandFeature(state, brandId, featureId);
  const organizationHasFeature: boolean = OptionFP.isSome(organizationFeature);
  /**
   * There are several pieces of information that determine the behavior of this action.
   * 1. enabled -- If the feature is being turned on or off.
   * 2. organizationHasFeature -- If this site feature is going to be overriding behavior in the org.
   * 3. existingBrandFeature -- If the feature already existed on the brand or not.
   *
   * In general, when setting a feature's state, if the merge diff is meaningful the BrandSiteFeature
   * entity is created or updated. When the brand site feature could be deleted and the resulting
   * behavior would be the same, the feature is deleted.
   */
  if (enabled) {
    if (organizationHasFeature) {
      // If the organization is enabled and we're setting this enabled, the simplest thing to do is
      // to delete the feature altogether. It would be possible also to check if the brand feature
      // existed, but in that case this would sort of be a no-op and there are legitmately situations
      // where we would want to remove the default behavior and return it to the default state (which
      // is what removing the value will do). In order to keep the UI simple, this will delete the
      // value and return the feature to it's default state (inheriting from Organization).
      dispatch(removeBrandSiteFeature(brandId, featureId));
    } else {
      pipe(
        brandFeature,
        // Ensure that some feature entity exists.
        OptionFP.getOrElse(() => {
          const createAction = dispatch(
            createAndAddBrandSiteFeature(brandId, {
              siteFeature: getSiteFeatureById(featureId),
              enabled,
            }),
          );
          return getEntityOfTypeOrThrow(getState(), BrandSiteFeature, createAction.id);
        }),
        existingBrandFeature => {
          // Set the feature as enabled
          dispatch(
            setAttribute({
              type: BrandSiteFeature,
              id: existingBrandFeature.id,
              attribute: 'enabled',
              value: enabled,
            }),
          );
          const configId = dispatch(createConfigForBrandSiteFeature(featureId, brandId));
          // Because the organization does not have this feature, an "override" for the config
          // is required in this branch so one is created.
          dispatch(
            setAttribute({
              type: BrandSiteFeature,
              id: existingBrandFeature.id,
              attribute: 'configuration',
              value: configId,
            }),
          );
        },
      );
    }
  } else {
    if (organizationHasFeature) {
      // Disabling an active feature on this brand.
      pipe(
        brandFeature,
        OptionFP.fold(
          () => {
            dispatch(
              // Here we can create and set the feature disabled since no feature previously existed.
              createAndAddBrandSiteFeature(brandId, {
                siteFeature: getSiteFeatureById(featureId),
                enabled: false,
              }),
            );
          },
          f => {
            dispatch(
              batchActions([
                // set the existing feature to disabled. In the current UI this state is somewhat unexpected
                // although it doesn't seem wrong for this behavior to exist. It's possible that this situation
                // could arise from values present in the database.
                setAttribute({
                  type: BrandSiteFeature,
                  id: f.id,
                  attribute: 'enabled',
                  value: enabled,
                }),
                // The feature may have a configuration so manually set that to null
                setAttribute({
                  type: BrandSiteFeature,
                  id: f.id,
                  attribute: 'configuration',
                  value: null,
                }),
              ]),
            );
          },
        ),
      );
    } else {
      // The organization does not have the feature that is being turned off. In this case, the
      // brand feature isn't useful so it may just be deleted.
      dispatch(removeBrandSiteFeature(brandId, featureId));
    }
  }
};

/**
 * Add an override config for a given site feature.
 * @param brandId -- the id of the brand on which to set the override.
 * @param featureId -- the id of the feature to override.
 * @returns thunkAction -- action to create the override.
 */
export const overrideBrandSiteFeature = (brandId: EntityStringId, featureId: SiteFeatureId): AppThunk<void> => (
  dispatch,
  getState,
) => {
  const state = getState();
  pipe(
    getBrandFeature(state, brandId, featureId),
    // Ensure that a brand feature entity exists to override.
    OptionFP.getOrElse(() => {
      const createAction = dispatch(
        createAndAddBrandSiteFeature(brandId, {
          siteFeature: getSiteFeatureById(featureId),
          enabled: true,
        }),
      );
      return getEntityOfTypeOrThrow(getState(), BrandSiteFeature, createAction.id);
    }),
    existingBrandSiteFeature => {
      // create a config for this brand site feature.
      const configId = dispatch(createConfigForBrandSiteFeature(featureId, brandId));
      dispatch(
        setAttribute({
          type: BrandSiteFeature,
          id: existingBrandSiteFeature.id,
          value: configId,
          attribute: 'configuration',
        }),
      );
    },
  );
};

/**
 * Remove an override from the brand site feature.
 * @param brandId -- the brand on which to remove the override
 * @param featureId -- the id of the feature from which to remove the config.
 * @returns thunkAction for removing the configuration.
 */
export const removeBrandSiteFeatureOverride = (brandId: EntityStringId, featureId: SiteFeatureId): AppThunk<void> => (
  dispatch,
  getState,
) => {
  const state = getState();
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const orgHasFeature = OptionFP.isSome(getOrganizationFeature(state, brand.organization, featureId));
  pipe(
    getBrandFeature(state, brandId, featureId),
    OptionFP.map(v => {
      if (orgHasFeature) {
        dispatch(removeBrandSiteFeature(brandId, featureId));
      } else {
        // This case is slightly unnecessary since if the organization doesn't have the
        // feature to begin with, there shouldn't be a way to remove the override, but it's possible
        // that the UI will change to allow this (as the backend doesn't explicitly disallow it).
        dispatch(
          setAttribute({
            type: BrandSiteFeature,
            id: v.id,
            value: null,
            attribute: 'configuration',
          }),
        );
      }
    }),
  );
};
